/**
 * MISSION: Consolidated Realtime channel per user.
 * Multiplexes 5 separate postgres_changes subscriptions into 2 Supabase channels:
 *   - user-root:${userId}: notifications, teacher_feedback, subscriptions (x2 events),
 *     speaking_guarantee_enrollments, chat_participants (membership tracking)
 *   - user-clan:${userId}: chat_messages filtered by channel_id IN (participants)
 *
 * Lazy lifecycle: first onX() call opens channels; refcount drops to 0 → cleanup after 2s.
 * Idempotent in React StrictMode dev double-mount.
 *
 * Saves ~3 connections per user → ~3.5x more concurrent users on Supabase free tier.
 */

import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';
import { Logger } from '@theidiomaclub/logging';

// ─── PUBLIC TYPES ──────────────────────────────────────────────────

export type UnsubscribeFn = () => void;
export type RealtimeRow = Record<string, unknown>;
export type AccessChangeEvent = 'INSERT' | 'UPDATE';

interface Stats {
    readonly rootChannels: number;
    readonly participantChannels: number;
    readonly participantCount: number;
    readonly connectedUserId: string | null;
}

// ─── INTERNAL STATE ────────────────────────────────────────────────

type RowCb = (row: RealtimeRow) => void;
type AccessCb = (row: RealtimeRow, event: AccessChangeEvent) => void;

interface InternalState {
    userId: string | null;
    rootChannel: RealtimeChannel | null;
    clanChannel: RealtimeChannel | null;
    participantChannelIds: readonly string[];
    fallbackMode: boolean;
    cleanupTimer: ReturnType<typeof setTimeout> | null;
    refreshDebounce: ReturnType<typeof setTimeout> | null;
    callbacks: {
        notification: Set<RowCb>;
        clanMessage: Set<RowCb>;
        teacherFeedback: Set<RowCb>;
        access: Set<AccessCb>;
        guarantee: Set<RowCb>;
    };
}

const state: InternalState = {
    userId: null,
    rootChannel: null,
    clanChannel: null,
    participantChannelIds: [],
    fallbackMode: false,
    cleanupTimer: null,
    refreshDebounce: null,
    callbacks: {
        notification: new Set(),
        clanMessage: new Set(),
        teacherFeedback: new Set(),
        access: new Set(),
        guarantee: new Set(),
    },
};

const MAX_FILTER_IDS = 90;
const CLEANUP_DELAY_MS = 2_000;
const REFRESH_DEBOUNCE_MS = 500;

// ─── INTERNAL HELPERS ──────────────────────────────────────────────

function totalListeners(): number {
    const c = state.callbacks;
    return c.notification.size + c.clanMessage.size + c.teacherFeedback.size + c.access.size + c.guarantee.size;
}

function emit(setRef: Set<RowCb>, payload: RealtimePostgresChangesPayload<RealtimeRow>): void {
    const row = payload.new as RealtimeRow | undefined;
    if (!row || typeof row !== 'object') return;
    setRef.forEach(cb => {
        try { cb(row); } catch (err) { Logger.warn('userRealtimeChannel: callback threw', err); }
    });
}

function emitAccess(payload: RealtimePostgresChangesPayload<RealtimeRow>, event: AccessChangeEvent): void {
    const row = payload.new as RealtimeRow | undefined;
    if (!row || typeof row !== 'object') return;
    state.callbacks.access.forEach(cb => {
        try { cb(row, event); } catch (err) { Logger.warn('userRealtimeChannel: access callback threw', err); }
    });
}

async function fetchParticipantChannels(userId: string): Promise<readonly string[]> {
    const db = getSupabaseClient();
    if (!db) return [];
    const { data, error } = await db
        .from('chat_participants')
        .select('channel_id')
        .eq('user_id', userId);
    if (error) {
        Logger.warn('userRealtimeChannel: fetchParticipantChannels failed', error.message);
        return [];
    }
    return (data ?? []).map(r => r.channel_id);
}

function openRootChannel(userId: string): void {
    const db = getSupabaseClient();
    if (!db) {
        Logger.warn('userRealtimeChannel: Supabase client not ready');
        return;
    }

    const userFilter = `user_id=eq.${userId}`;
    const teacherFilter = `student_id=eq.${userId}`;

    state.rootChannel = db
        .channel(`user-root:${userId}`)
        .on('postgres_changes' as never, { event: 'INSERT', schema: 'public', table: 'notifications', filter: userFilter },
            (p: RealtimePostgresChangesPayload<RealtimeRow>) => emit(state.callbacks.notification, p))
        .on('postgres_changes' as never, { event: 'INSERT', schema: 'public', table: 'teacher_feedback', filter: teacherFilter },
            (p: RealtimePostgresChangesPayload<RealtimeRow>) => emit(state.callbacks.teacherFeedback, p))
        .on('postgres_changes' as never, { event: 'INSERT', schema: 'public', table: 'subscriptions', filter: userFilter },
            (p: RealtimePostgresChangesPayload<RealtimeRow>) => emitAccess(p, 'INSERT'))
        .on('postgres_changes' as never, { event: 'UPDATE', schema: 'public', table: 'subscriptions', filter: userFilter },
            (p: RealtimePostgresChangesPayload<RealtimeRow>) => emitAccess(p, 'UPDATE'))
        .on('postgres_changes' as never, { event: '*', schema: 'public', table: 'speaking_guarantee_enrollments', filter: userFilter },
            (p: RealtimePostgresChangesPayload<RealtimeRow>) => emit(state.callbacks.guarantee, p))
        .on('postgres_changes' as never, { event: 'INSERT', schema: 'public', table: 'chat_participants', filter: userFilter },
            () => scheduleRefresh())
        .on('postgres_changes' as never, { event: 'DELETE', schema: 'public', table: 'chat_participants', filter: userFilter },
            () => scheduleRefresh())
        .subscribe();
}

async function openClanChannel(userId: string): Promise<void> {
    const db = getSupabaseClient();
    if (!db) return;

    const ids = await fetchParticipantChannels(userId);
    state.participantChannelIds = ids;

    if (ids.length === 0) {
        state.clanChannel = null;
        state.fallbackMode = false;
        return;
    }

    const useFilter = ids.length <= MAX_FILTER_IDS;
    state.fallbackMode = !useFilter;
    if (!useFilter) {
        Logger.warn('userRealtimeChannel: filter fallback mode (client-side)', { count: ids.length });
    }

    const config: { event: 'INSERT'; schema: string; table: string; filter?: string } = {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
    };
    if (useFilter) config.filter = `channel_id=in.(${ids.join(',')})`;

    const idSet = new Set(ids);
    state.clanChannel = db
        .channel(`user-clan:${userId}`)
        .on('postgres_changes' as never, config, (p: RealtimePostgresChangesPayload<RealtimeRow>) => {
            const row = p.new as RealtimeRow | undefined;
            if (!row) return;
            if (!useFilter) {
                const cid = row.channel_id as string | undefined;
                if (!cid || !idSet.has(cid)) return;
            }
            emit(state.callbacks.clanMessage, p);
        })
        .subscribe();
}

function scheduleRefresh(): void {
    if (state.refreshDebounce) clearTimeout(state.refreshDebounce);
    state.refreshDebounce = setTimeout(() => {
        state.refreshDebounce = null;
        void refreshParticipantChannels();
    }, REFRESH_DEBOUNCE_MS);
}

async function refreshParticipantChannels(): Promise<void> {
    if (!state.userId) return;
    const db = getSupabaseClient();
    if (!db) return;
    if (state.clanChannel) {
        await db.removeChannel(state.clanChannel);
        state.clanChannel = null;
    }
    await openClanChannel(state.userId);
}

async function ensureConnected(userId: string): Promise<void> {
    if (state.cleanupTimer) {
        clearTimeout(state.cleanupTimer);
        state.cleanupTimer = null;
    }
    if (state.userId === userId && state.rootChannel) return;
    if (state.userId && state.userId !== userId) {
        await disconnect();
    }
    state.userId = userId;

    const { waitForClient } = await import('./supabase');
    const db = await waitForClient();
    if (!db) {
        Logger.warn('userRealtimeChannel: Supabase client could not be initialized');
        return;
    }

    if (state.userId !== userId) {
        return;
    }

    openRootChannel(userId);
    await openClanChannel(userId);
}

function scheduleCleanup(): void {
    if (state.cleanupTimer) return;
    state.cleanupTimer = setTimeout(() => {
        state.cleanupTimer = null;
        if (totalListeners() === 0) void disconnect();
    }, CLEANUP_DELAY_MS);
}

async function disconnect(): Promise<void> {
    const db = getSupabaseClient();
    if (state.refreshDebounce) { clearTimeout(state.refreshDebounce); state.refreshDebounce = null; }
    if (state.cleanupTimer) { clearTimeout(state.cleanupTimer); state.cleanupTimer = null; }
    if (db) {
        if (state.rootChannel) await db.removeChannel(state.rootChannel);
        if (state.clanChannel) await db.removeChannel(state.clanChannel);
    }
    state.rootChannel = null;
    state.clanChannel = null;
    state.userId = null;
    state.participantChannelIds = [];
    state.fallbackMode = false;
}

function register<T>(setRef: Set<T>, cb: T, userId: string): UnsubscribeFn {
    setRef.add(cb);
    void ensureConnected(userId);
    let removed = false;
    return () => {
        if (removed) return;
        removed = true;
        setRef.delete(cb);
        if (totalListeners() === 0) scheduleCleanup();
    };
}

// ─── PUBLIC API ────────────────────────────────────────────────────

export const userRealtimeChannel = {
    onNotification(userId: string, cb: RowCb): UnsubscribeFn {
        return register(state.callbacks.notification, cb, userId);
    },
    onClanMessage(userId: string, cb: RowCb): UnsubscribeFn {
        return register(state.callbacks.clanMessage, cb, userId);
    },
    onTeacherFeedback(userId: string, cb: RowCb): UnsubscribeFn {
        return register(state.callbacks.teacherFeedback, cb, userId);
    },
    onAccessChange(userId: string, cb: AccessCb): UnsubscribeFn {
        return register(state.callbacks.access, cb, userId);
    },
    onGuaranteeChange(userId: string, cb: RowCb): UnsubscribeFn {
        return register(state.callbacks.guarantee, cb, userId);
    },
    refreshParticipantChannels,
    disconnect,
    getStats(): Stats {
        return {
            rootChannels: state.rootChannel ? 1 : 0,
            participantChannels: state.clanChannel ? 1 : 0,
            participantCount: state.participantChannelIds.length,
            connectedUserId: state.userId,
        };
    },
} as const;

// DevTools convenience — only in development
if (import.meta.env.DEV && typeof window !== 'undefined') {
    (window as unknown as { __rtStats?: () => Stats }).__rtStats = () => userRealtimeChannel.getStats();
}
